import { ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, Note, Participant, SourceData, SourceDataReview, SourceDataVerification, Trial } from "@/api/dtos";
import useEvent from "@/hooks/form/source-data/useEvent";
import useForm from "@/hooks/form/source-data/useForm";
import useItemsVirtualized from "@/hooks/form/source-data/useItemsVirtualized";
import { Paper } from "@mui/material";
import { FC, useMemo, useState } from "react";
import ReviewAndVerificationForm from "../../ReviewAndVerificationForm";
import SourceDataFormVirtualizedList from "./lists/SourceDataFormVirtualizedList";
import SourceDataFormReviewAndVerificationAllButton from "./buttons/SourceDataFormReviewAndVerificationAllButton";
import useEventConfiguration from "@/hooks/form/configuration/useEventConfiguration";
import useFormConfiguration from "@/hooks/form/configuration/useFormConfiguration";
import useFormVersionConfiguration from "@/hooks/form/configuration/useFormVersionConfiguration";
import useSourceDataItemGroupGrid from "@/hooks/form/source-data/useSourceDataItemGroupGrid";
import SourceDataItemGroupGrid from "./grids/SourceDataItemGroupGrid";
import SourceDataItemGroupGridContext from "@/contexts/form/SourceDataItemGroupGridContext";
import { ItemType } from "@/hooks/form/configuration/useItemConfigurations";


interface ISourceDataFormProps {
    trial: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant: Participant;
    metadata: Metadata;
    trialConfiguration: ConfigurationSourceDataMap;
    configuration: ConfigurationSourceDataMap;
    sourceData?: SourceData;
    studyEventOid: string;
    studyEventRepeat: number;
    caseReportFormOid: string;
    caseReportFormVersionOid: string;
    notesAndQueries?: Record<string, Record<number, Record<string, Record<string, Record<number, Record<string, Note[]>>>>>>;
    preview?: boolean;
    reviews?: Record<string, Record<number, Record<string, Record<string, Record<number, Record<string, SourceDataReview>>>>>>;
    reviewsUpdating?: boolean;
    verifications?: Record<string, Record<number, Record<string, Record<string, Record<number, Record<string, SourceDataVerification>>>>>>;
    verificationsUpdating?: boolean;

    form: ReturnType<typeof useForm>;
    event: ReturnType<typeof useEvent>;
}


const SourceDataForm: FC<ISourceDataFormProps> = ({
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,
    metadata,
    trialConfiguration,
    configuration,
    sourceData,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    event,
    form,
    preview
}) => {

    // const itemData = useItems(
    //     metadata,
    //     trialConfiguration,
    //     configuration,
    //     sourceData,
    //     trial,
    //     masterGroup,
    //     coordinatingGroup,
    //     institution,
    //     participant,
    //     studyEventOid,
    //     studyEventRepeat,
    //     caseReportFormOid,
    //     caseReportFormVersionOid,
    //     preview
    // );

    const itemData = useItemsVirtualized(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        preview
    );


    const eventConfiguration = useEventConfiguration(
        metadata,
        trialConfiguration,
        configuration,
        trial,
        configuration?.configuration?.id,
        studyEventOid
    );

    const formConfiguration = useFormConfiguration(
        metadata,
        trialConfiguration,
        configuration,
        trial,
        configuration?.configuration?.id,
        studyEventOid,
        caseReportFormOid
    );

    const formVersionConfiguration = useFormVersionConfiguration(
        metadata,
        trialConfiguration,
        configuration,
        trial,
        configuration?.configuration?.id,
        studyEventOid,
        caseReportFormOid,
        caseReportFormVersionOid
    );

    const allowed = useMemo(() => {
        const configurationAllowReview = !form?.excluded &&
            eventConfiguration?.configuration?.allowReview === true && 
            formConfiguration?.configuration?.allowReview === true &&
            formVersionConfiguration?.configuration?.allowReview === true;

        const configurationAllowVerification = !form?.excluded &&
            eventConfiguration?.configuration?.allowVerification === true && 
            formConfiguration?.configuration?.allowVerification === true &&
            formVersionConfiguration?.configuration?.allowVerification === true;

        const configurationAllowNotApplicable = !form?.excluded &&
            eventConfiguration?.configuration?.allowNotApplicable === true && 
            formConfiguration?.configuration?.allowNotApplicable === true &&
            formVersionConfiguration?.configuration?.allowNotApplicable === true;

        return {
            configurationAllowReview,
            configurationAllowVerification,
            configurationAllowNotApplicable
        }
    }, [eventConfiguration, formConfiguration, formVersionConfiguration, form?.excluded]);

    const allowReview = useMemo(() => {
        if(!allowed.configurationAllowReview) {
            return false;
        }

        if(!itemData) {
            return false;
        }

        for(let row of itemData) {
            if(row.type === ItemType.ItemGroup) {
                continue;
            }

            if((!row.itemGroup || row.itemGroup.configuration?.allowReview) && row.item.configuration?.allowReview) {
                return true;
            }
        }

        return false;
    }, [allowed.configurationAllowReview, itemData]);

    const allowVerification = useMemo(() => {
        if(!allowed.configurationAllowVerification) {
            return false;
        }

        if(!itemData) {
            return false;
        }

        for(let row of itemData) {
            if(row.type === ItemType.ItemGroup) {
                continue;
            }

            if((!row.itemGroup || row.itemGroup.configuration?.allowVerification) && row.item.configuration?.allowVerification) {
                return true;
            }
        }

        return false;
    }, [allowed.configurationAllowReview, itemData]);

    const allowNotApplicable = useMemo(() => {
        if(!allowed.configurationAllowNotApplicable) {
            return false;
        }

        if(!itemData) {
            return false;
        }

        for(let row of itemData) {
            if(row.type === ItemType.ItemGroup) {
                continue;
            }

            if((!row.itemGroup || row.itemGroup.configuration?.allowNotApplicable) && row.item.configuration?.allowNotApplicable) {
                return true;
            }
        }

        return false;
    }, [allowed.configurationAllowNotApplicable, itemData]);

    const [itemGroupName, setItemGroupName] = useState<string | undefined>();

    const context = useMemo(() => {
        return {
            itemGroupName, 
            setItemGroupName
        }
    }, [itemGroupName, setItemGroupName])

    if (!itemData) {
        return null;
    }

    return (
        <ReviewAndVerificationForm
            trialId={trial?.id}
            masterGroupId={masterGroup?.id}
            coordinatingGroupId={coordinatingGroup?.id}
            institutionId={institution?.id}
            participantId={participant?.id}
            studyEventOid={studyEventOid}
            studyEventRepeat={studyEventRepeat}
            caseReportFormOid={caseReportFormOid}
        >
            <SourceDataItemGroupGrid 
                metadata={metadata}
                trialConfiguration={trialConfiguration}
                configuration={configuration}
                sourceData={sourceData}
                trial={trial}
                masterGroup={masterGroup}
                coordinatingGroup={coordinatingGroup}
                institution={institution}
                participant={participant}
                studyEventOid={studyEventOid}
                studyEventRepeat={studyEventRepeat}
                caseReportFormOid={caseReportFormOid}
                caseReportFormVersionOid={caseReportFormVersionOid}
                itemGroupName={itemGroupName}
                preview={preview}
                onClose={() => setItemGroupName(undefined)}
            />
            <SourceDataFormReviewAndVerificationAllButton
                form={form}
                trial={trial}
                masterGroup={masterGroup}
                coordinatingGroup={coordinatingGroup}
                institution={institution}
                participant={participant}
                studyEventOid={studyEventOid}
                studyEventRepeat={studyEventRepeat}
                caseReportFormOid={caseReportFormOid}
                caseReportFormVersionOid={caseReportFormVersionOid}
                itemData={itemData}
                allowReview={allowReview}
                allowVerification={allowVerification}
                allowNotApplicable={allowNotApplicable}
            />
            <SourceDataItemGroupGridContext.Provider value={context}>
                <Paper>
                    {/* <SourceDataItemList itemData={itemData} /> */}
                    <SourceDataFormVirtualizedList 
                        itemData={itemData} 
                        form={form} 
                        event={event} 
                        allowReview={allowReview}
                        allowVerification={allowVerification}
                        allowNotApplicable={allowNotApplicable}
                    />
                </Paper>
            </SourceDataItemGroupGridContext.Provider>
        </ReviewAndVerificationForm>
    )
};

export default SourceDataForm;